import { takeLatest, put, call, all, fork } from 'redux-saga/effects';
import {
	fetchLeaveRestrictions,
	createLeaveRestriction,
	updateLeaveRestriction,
	deleteLeaveRestriction,
	unpublishLeaveRestriction,
	publishLeaveRestriction,
} from './leaveRestriction.services';
import LeaveRestrictionTypes from './leaveRestriction.types';
import {
	fetchLeaveRestrictionsSuccess,
	fetchLeaveRestrictionsFailure,
	createLeaveRestrictionSuccess,
	createLeaveRestrictionFailure,
	updateLeaveRestrictionSuccess,
	updateLeaveRestrictionFailure,
	deleteLeaveRestrictionSuccess,
	deleteLeaveRestrictionFailure,
	unpublishLeaveRestrictionSuccess,
	unpublishLeaveRestrictionFailure,
	publishLeaveRestrictionSuccess,
	publishLeaveRestrictionFailure,
} from './leaveRestriction.action';
import AlertManager from '../../utils/alertManager';

const getApiData = (response: any) => (response && response.data ? response.data : response);

const ensureSuccess = (apiData: any, failureAction: (payload: any) => any) => {
	if (apiData?.status === 'error') {
		return failureAction(apiData.message_text || 'An error occurred');
	}
	return null;
};

// Fetch Leave Restrictions Saga
export function* fetchLeaveRestrictionsSaga(action?: any) {
	try {
		const requestPayload = action?.payload ?? {};
		// @ts-ignore
		const responseData = yield call(fetchLeaveRestrictions as any, requestPayload);
		const apiData = getApiData(responseData);
		if (apiData?.statusCode === '401') {
			// Show token validation alert message
			AlertManager.showTokenValidationAlert();
			return;
		}

		const failure = ensureSuccess(apiData, fetchLeaveRestrictionsFailure);
		if (failure) {
			yield put(failure);
			return;
		}

		yield put(fetchLeaveRestrictionsSuccess(apiData));
	} catch (error: any) {
		yield put(
			fetchLeaveRestrictionsFailure(error?.response?.data?.message || 'An error occurred'),
		);
	}
}

// Create Leave Restriction Saga
export function* createLeaveRestrictionSaga(action: any) {
	try {
		const requestPayload = action?.payload ?? {};
		// @ts-ignore
		const responseData = yield call(createLeaveRestriction as any, requestPayload);
		const apiData = getApiData(responseData);
		if (apiData?.statusCode === '401') {
			// Show token validation alert
			AlertManager.showTokenValidationAlert();
			return;
		}
		const failure = ensureSuccess(apiData, createLeaveRestrictionFailure);
		if (failure) {
			yield put(failure);
			return;
		}
		yield put(createLeaveRestrictionSuccess(apiData));
	} catch (error: any) {
		yield put(
			createLeaveRestrictionFailure(error?.response?.data?.message || 'An error occurred'),
		);
	}
}

// Update Leave Restriction Saga
export function* updateLeaveRestrictionSaga(action: any) {
	try {
		const requestPayload = action?.payload ?? {};
		// @ts-ignore
		const responseData = yield call(updateLeaveRestriction as any, requestPayload);
		const apiData = getApiData(responseData);
		if (apiData?.statusCode === '401') {
			// Show token validation alert
			AlertManager.showTokenValidationAlert();
			return;
		}
		const failure = ensureSuccess(apiData, updateLeaveRestrictionFailure);
		if (failure) {
			yield put(failure);
			return;
		}
		yield put(updateLeaveRestrictionSuccess(apiData));
	} catch (error: any) {
		yield put(
			updateLeaveRestrictionFailure(error?.response?.data?.message || 'An error occurred'),
		);
	}
}

// Delete Leave Restriction Saga
export function* deleteLeaveRestrictionSaga(action: any) {
	try {
		// action.payload is the restrictLeaveID number
		const restrictLeaveID = action?.payload;
		if (!restrictLeaveID) {
			yield put(deleteLeaveRestrictionFailure('restrictLeaveID is required'));
			return;
		}
		// @ts-ignore
		const responseData = yield call(deleteLeaveRestriction as any, restrictLeaveID);
		const apiData = getApiData(responseData);
		if (apiData?.statusCode === '401') {
			// Show token validation alert
			AlertManager.showTokenValidationAlert();
			return;
		}
		const failure = ensureSuccess(apiData, deleteLeaveRestrictionFailure);
		if (failure) {
			yield put(failure);
			return;
		}
		yield put(deleteLeaveRestrictionSuccess(restrictLeaveID));
	} catch (error: any) {
		yield put(
			deleteLeaveRestrictionFailure(error?.response?.data?.message || 'An error occurred'),
		);
	}
}

// Unpublish Leave Restriction Saga
export function* unpublishLeaveRestrictionSaga(action: any) {
	try {
		const requestPayload = action?.payload ?? {};
		// @ts-ignore
		const responseData = yield call(unpublishLeaveRestriction as any, requestPayload);
		const apiData = getApiData(responseData);
		if (apiData?.statusCode === '401') {
			// Show token validation alert
			AlertManager.showTokenValidationAlert();
			return;
		}
		const failure = ensureSuccess(apiData, unpublishLeaveRestrictionFailure);
		if (failure) {
			yield put(failure);
			return;
		}
		yield put(unpublishLeaveRestrictionSuccess(action.payload));
	} catch (error: any) {
		yield put(
			unpublishLeaveRestrictionFailure(error?.response?.data?.message || 'An error occurred'),
		);
	}
}

// Publish Leave Restriction Saga
export function* publishLeaveRestrictionSaga(action: any) {
	try {
		const requestPayload = action?.payload ?? {};
		// @ts-ignore
		const responseData = yield call(publishLeaveRestriction as any, requestPayload);
		const apiData = getApiData(responseData);
		if (apiData?.statusCode === '401') {
			// Show token validation alert
			AlertManager.showTokenValidationAlert();
			return;
		}
		const failure = ensureSuccess(apiData, publishLeaveRestrictionFailure);
		if (failure) {
			yield put(failure);
			return;
		}
		yield put(publishLeaveRestrictionSuccess(action.payload));
	} catch (error: any) {
		yield put(
			publishLeaveRestrictionFailure(error?.response?.data?.message || 'An error occurred'),
		);
	}
}

// Watcher Sagas
export function* onFetchLeaveRestrictionsStart(): Generator<any, void, any> {
	yield takeLatest(
		LeaveRestrictionTypes.FETCH_LEAVE_RESTRICTIONS_START as any,
		fetchLeaveRestrictionsSaga,
	);
}

export function* onCreateLeaveRestrictionStart(): Generator<any, void, any> {
	yield takeLatest(
		LeaveRestrictionTypes.CREATE_LEAVE_RESTRICTION_START as any,
		createLeaveRestrictionSaga,
	);
}

export function* onUpdateLeaveRestrictionStart(): Generator<any, void, any> {
	yield takeLatest(
		LeaveRestrictionTypes.UPDATE_LEAVE_RESTRICTION_START as any,
		updateLeaveRestrictionSaga,
	);
}

export function* onDeleteLeaveRestrictionStart(): Generator<any, void, any> {
	yield takeLatest(
		LeaveRestrictionTypes.DELETE_LEAVE_RESTRICTION_START as any,
		deleteLeaveRestrictionSaga,
	);
}

export function* onUnpublishLeaveRestrictionStart(): Generator<any, void, any> {
	yield takeLatest(
		LeaveRestrictionTypes.UNPUBLISH_LEAVE_RESTRICTION_START as any,
		unpublishLeaveRestrictionSaga,
	);
}

export function* onPublishLeaveRestrictionStart(): Generator<any, void, any> {
	yield takeLatest(
		LeaveRestrictionTypes.PUBLISH_LEAVE_RESTRICTION_START as any,
		publishLeaveRestrictionSaga,
	);
}

// Main Saga
export function* leaveRestrictionSagaDetails(): Generator<any, void, any> {
	yield all([
		fork(onFetchLeaveRestrictionsStart),
		fork(onCreateLeaveRestrictionStart),
		fork(onUpdateLeaveRestrictionStart),
		fork(onDeleteLeaveRestrictionStart),
		fork(onUnpublishLeaveRestrictionStart),
		fork(onPublishLeaveRestrictionStart),
	]);
}
